import { MapPin, Eye, Key, Copy, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const getStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'bg-green-500';
    case 'inactive':
      return 'bg-gray-500';
    case 'maintenance':
      return 'bg-yellow-500';
    default:
      return 'bg-gray-500';
  }
};

const getAgentStatus = (lastHeartbeat) => {
  if (!lastHeartbeat) return { online: false, text: 'Never connected', color: 'text-gray-500' };
  
  const lastBeat = new Date(lastHeartbeat);
  const now = new Date();
  const diffMinutes = (now - lastBeat) / (1000 * 60);
  
  if (diffMinutes < 2) {
    return { online: true, text: 'Online', color: 'text-green-600' };
  } else if (diffMinutes < 5) {
    return { online: true, text: `${Math.floor(diffMinutes)}m ago`, color: 'text-yellow-600' };
  } else {
    return { online: false, text: `Offline (${Math.floor(diffMinutes)}m)`, color: 'text-red-500' };
  }
};

const KioskCard = ({ kiosk, onViewDetails, onCopyKey, onRegenerateKey }) => {
  const agentStatus = getAgentStatus(kiosk.last_heartbeat);
  
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`kiosk-${kiosk.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg font-heading">{kiosk.name}</CardTitle>
            <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
              <MapPin className="w-3 h-3" />
              {kiosk.location}
            </div>
          </div>
          <div className={`w-3 h-3 rounded-full ${getStatusColor(kiosk.status)}`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Printer IP:</span>
            <span className="font-mono">{kiosk.printer_ip}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Status:</span>
            <span className="capitalize font-medium">{kiosk.status}</span>
          </div>
          {/* Agent Status */}
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Print Agent:</span>
            <span className={`flex items-center gap-1 font-medium ${agentStatus.color}`}>
              {agentStatus.online ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {agentStatus.text}
            </span>
          </div>
          {kiosk.printer_status && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Printer:</span>
              <span className="capitalize font-medium">{kiosk.printer_status}</span>
            </div>
          )}
        </div>
        
        {/* API Key Section */}
        <div className="mt-4 p-3 bg-secondary rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Key className="w-3 h-3" />
              Agent API Key
            </span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => onCopyKey(kiosk.api_key)}
                title="Copy API Key"
              >
                <Copy className="w-3 h-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-orange-500 hover:text-orange-600"
                onClick={() => onRegenerateKey(kiosk.id)}
                title="Regenerate API Key"
              >
                <RefreshCw className="w-3 h-3" />
              </Button>
            </div>
          </div>
          <code className="text-xs font-mono break-all text-muted-foreground">
            {kiosk.api_key?.slice(0, 20)}...
          </code>
        </div>
        
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full mt-4"
          onClick={() => onViewDetails(kiosk)}
          data-testid={`view-kiosk-${kiosk.id}`}
        >
          <Eye className="w-4 h-4 mr-2" />
          View Details
        </Button>
      </CardContent>
    </Card>
  );
};

const KiosksGrid = ({ kiosks, onViewDetails, onCopyKey, onRegenerateKey }) => {
  return (
    <Card className="mb-8" data-testid="kiosks-section">
      <CardHeader>
        <CardTitle className="font-heading text-xl">Kiosks</CardTitle>
        <CardDescription>Manage your print kiosk network</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kiosks.map((kiosk) => (
            <KioskCard
              key={kiosk.id}
              kiosk={kiosk}
              onViewDetails={onViewDetails}
              onCopyKey={onCopyKey}
              onRegenerateKey={onRegenerateKey}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default KiosksGrid;
